import { Button, Flex, Modal, Typography } from 'antd';
import {
  IconAlertTriangle,
  IconDeviceFloppy,
  IconInfoCircle,
  IconTrash,
} from '@tabler/icons-react';
import type { ComponentType, ReactNode } from 'react';

const { Paragraph, Title } = Typography;

export type ConfirmDialogVariant = 'danger' | 'warning' | 'primary' | 'info';

export type ConfirmDialogProps = {
  open: boolean;
  variant?: ConfirmDialogVariant;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  loading?: boolean;
  width?: number;
  onCancel: () => void;
  onConfirm: () => void;
  children?: ReactNode;
};

const VARIANT_ICONS: Record<
  ConfirmDialogVariant,
  ComponentType<{ size?: number; stroke?: number }>
> = {
  danger: IconTrash,
  warning: IconAlertTriangle,
  primary: IconDeviceFloppy,
  info: IconInfoCircle,
};

//Shared component for confirm dialogs
export function ConfirmDialog({
  open,
  variant = 'primary',
  title,
  description,
  confirmLabel,
  cancelLabel,
  loading,
  width = 480,
  onCancel,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const Icon = VARIANT_ICONS[variant];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      closable={!loading}
      mask={{ closable: !loading }}
      destroyOnHidden
      className={`app-confirm-dialog app-confirm-dialog--${variant}`}
      width={width}
      title={null}
      footer={
        <Flex justify="flex-end" gap={8} className="app-confirm-dialog-footer">
          <Button onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            type="primary"
            danger={variant === 'danger'}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Flex>
      }
    >
      <Flex gap={16} align="flex-start" className="app-confirm-dialog-body">
        <span
          className={`app-confirm-dialog-icon app-confirm-dialog-icon--${variant}`}
          aria-hidden
        >
          <Icon size={24} stroke={1.75} />
        </span>

        <Flex vertical gap={12} className="app-confirm-dialog-content">
          <Flex vertical gap={6}>
            <Title level={4} className="app-confirm-dialog-title">
              {title}
            </Title>
            {description ? (
              <Paragraph className="app-confirm-dialog-description">
                {description}
              </Paragraph>
            ) : null}
          </Flex>
          {children}
        </Flex>
      </Flex>
    </Modal>
  );
}
